import { LOCALE_KEY } from "@/lib/local-storage.consts";
import type { User } from "@supabase/supabase-js";
import { Link } from "@tanstack/react-router";
import { ChevronRightIcon, HomeIcon } from "lucide-react";
import { useEffect } from "react";

import { KairoLogo } from "@/components/navigation/kairo-logo";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { useAuth, useSignOutMutation } from "@/features/auth";
import { ProfileAvatar } from "@/features/avatar";
import { getInitials } from "@/features/avatar/avatar.utils";
import { NotificationBell } from "@/features/notifications/components/notification-bell";
import { AVATARS_STORAGE_BUCKET, useProfileQuery } from "@/features/profiles";
import { ProjectCombobox } from "@/features/projects/components/project-combobox";
import { WorkspaceSearch } from "@/features/search/components/workspace-search";
import { isSuperuser } from "@/features/superuser/is-superuser";
import { WorkspaceCombobox } from "@/features/workspaces/components/workspace-combobox";

import { FeedbackAction } from "./feedback-action";
import { useHeaderContext } from "./use-header-context";

const HomeButton = () => (
  <Button variant="ghost" size="sm" className="h-9 w-9">
    <HomeIcon />
    <span className="sr-only">Home</span>
  </Button>
);

const HomeLink = ({ isIncognito, slug }: { isIncognito: boolean; slug: string | undefined }) => {
  if (isIncognito) {
    return (
      <Link to="/superuser/workspaces">
        <HomeButton />
      </Link>
    );
  }
  if (slug) {
    return (
      <Link to="/workspaces/$slug" params={{ slug }}>
        <HomeButton />
      </Link>
    );
  }
  return (
    <Link to="/workspaces">
      <HomeButton />
    </Link>
  );
};

interface HeaderContentProps {
  user: User;
  logoOnly?: boolean;
}

const HeaderContent = ({ user, logoOnly }: HeaderContentProps) => {
  const { data: profile } = useProfileQuery(user.id);
  const { mutate: signOut } = useSignOutMutation();
  const {
    slug,
    projectSlug,
    routeProjectSlug,
    workspaceName,
    routeProjectId,
    isIncognito,
    isContributor,
    isSettingsContext,
    settingsLinkSearch,
  } = useHeaderContext(user.id, !!logoOnly);
  // Explicit combobox props only for the Settings context; elsewhere the
  // comboboxes keep reading the route params themselves.
  const comboboxContext = isSettingsContext ? { workspaceSlug: slug, projectSlug } : {};
  useEffect(() => {
    if (!profile?.locale) return;
    try {
      window.localStorage.setItem(LOCALE_KEY, JSON.stringify(profile.locale));
      window.dispatchEvent(new StorageEvent("local-storage", { key: "locale" }));
    } catch {
      // ignore
    }
  }, [profile?.locale]);

  return (
    <header className="relative bg-card shadow-sm">
      {!!routeProjectSlug && !logoOnly && !isContributor && (
        <SidebarTrigger className="absolute top-3.5 left-2.5 h-9 w-9" />
      )}
      <div className="flex h-16 items-center justify-between pr-8 pl-12">
        <div className="flex items-center gap-6">
          {logoOnly ? (
            <Link to="/">
              <KairoLogo />
            </Link>
          ) : (
            <nav className="flex items-center gap-2">
              <HomeLink isIncognito={isIncognito} slug={slug} />
              {/* CR-116: the picker lists member workspaces only and would
                  leave incognito mode; the banner links back instead. */}
              {/* CR-226: the route-resolved name labels the selection even
                  before the member list has it. Only the workspace picker
                  gets it: ProjectCombobox's explicit mode is Settings-only. */}
              {!isIncognito && (
                <WorkspaceCombobox {...comboboxContext} workspaceName={workspaceName} />
              )}
              {!!slug && !isIncognito && (
                <>
                  <ChevronRightIcon className="size-4 text-muted-foreground" />
                  <ProjectCombobox {...comboboxContext} />
                </>
              )}
            </nav>
          )}
        </div>
        {/* CR-183 D8: a contributor has no search (they can read nothing it finds). */}
        {!!slug && !logoOnly && !isIncognito && !isContributor && (
          <WorkspaceSearch workspaceSlug={slug} currentProjectSlug={projectSlug} />
        )}
        <div className="flex items-center gap-4">
          {/* CR-302: no Feedback for a contributor; the api-server refuses that context too.
              Unlike Help, incognito keeps the project id: a superuser with no row there is not refused. */}
          {!logoOnly && !isContributor && <FeedbackAction projectId={routeProjectId} />}
          {/* CR-189 D5: Help opens the user's Kairo help chat; a contributor has no AI chats. */}
          {!logoOnly && !isContributor && (
            <Link
              to="/support"
              search={{ project: isIncognito ? undefined : routeProjectId }}
              className="hover:text-primary text-sm font-medium"
            >
              Help
            </Link>
          )}
          {isSuperuser(user.email) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="hover:text-primary [&.active]:text-primary text-sm font-medium"
                >
                  Admin
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuGroup>
                  <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                    Administration
                  </DropdownMenuLabel>
                  <DropdownMenuItem asChild>
                    <Link to="/superuser/invites">Invites</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/superuser/workspaces">Workspaces</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/superuser/skills">Skills</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/superuser/integrations">Integrations</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/superuser/models">Models</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/superuser/ai-settings">AI settings</Link>
                  </DropdownMenuItem>
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {!!profile && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="cursor-pointer transition-opacity hover:opacity-75"
                >
                  <ProfileAvatar
                    avatarPath={profile.avatar_url}
                    fallbackInitials={getInitials({
                      firstName: profile.first_name,
                      lastName: profile.last_name,
                      email: profile.email,
                    })}
                    size={40}
                    storageName={AVATARS_STORAGE_BUCKET}
                  />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuGroup>
                  <DropdownMenuItem asChild>
                    <Link
                      to="/account"
                      search={settingsLinkSearch}
                      className="cursor-pointer font-normal"
                    >
                      Settings
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  variant="destructive"
                  className="cursor-pointer"
                  onSelect={() => signOut()}
                >
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <NotificationBell userId={user.id} />
        </div>
      </div>
    </header>
  );
};

export const AuthenticatedHeader = () => {
  const { user } = useAuth();
  return <HeaderContent user={user} />;
};

export const InviteHeader = ({ user }: { user: User }) => {
  return <HeaderContent user={user} logoOnly />;
};
